/**
 * RPDM Wave 2-UI · Phase 2 · Delivery Hierarchy page.
 *
 * Read-only hierarchy tree page. Consumes the additive backend
 * endpoint `GET /api/projects/:engagementId/hierarchy` via the
 * `useHierarchyTree` hook. Renders one of five states:
 *
 *   1. Feature-flag OFF → `<FailClosedPlaceholder flagsFailed=[...]>`.
 *   2. Loading         → `<div role="status" aria-live="polite">`.
 *   3. Capability-missing → `<FailClosedPlaceholder capabilityMissing='hierarchy'>`.
 *   4. Other error     → `<FailClosedPlaceholder>` with the refusal
 *                        translation.
 *   5. Empty engagement → `<div data-testid="hierarchy-empty">`.
 *   6. Success        → `<HierarchyTree tree={...}>`.
 *
 * NO editable affordance is exposed under any state (§ Phase 2 scope).
 */
import { useAuth } from "../../../../contexts/AuthContext";
import { useRpdmWave2Gates } from "../../../../hooks/useRpdmWave2Gates";
import { useHierarchyTree } from "../../../../hooks/useHierarchyTree";
import { FailClosedPlaceholder } from "../../../../components/rpdm/FailClosedPlaceholder";
import { translate } from "../../../../lib/rpdm/refusalCodes";
import HierarchyTree from "./HierarchyTree";

function isEmpty(tree) {
    if (!tree) return true;
    const ws = Array.isArray(tree.workstreams) ? tree.workstreams.length : 0;
    const oms = Array.isArray(tree.orphan_milestones) ? tree.orphan_milestones.length : 0;
    const owi = Array.isArray(tree.orphan_work_items) ? tree.orphan_work_items.length : 0;
    return ws === 0 && oms === 0 && owi === 0;
}

export default function DeliveryHierarchyPage({ engagementId }) {
    const { user } = useAuth();
    const gates = useRpdmWave2Gates();
    const isSuperAdmin = user?.role === "super_admin";
    // Always call the hook — hooks must be invoked in the same order
    // every render. When the hierarchy flag is OFF we simply ignore
    // the hook's output; the hook itself guards with an early return
    // and issues no request.
    const flagOff = !gates.hierarchy;
    const { data, loading, error } = useHierarchyTree(flagOff ? null : engagementId);

    // Flag-off path — hierarchy content is fail-closed even though the
    // layout renders.
    if (flagOff) {
        const failed = ["rpdm_w2_hierarchy_enabled"];
        if (!gates.w2Master) failed.unshift("rpdm_w2_master_enabled");
        return (
            <FailClosedPlaceholder
                flagsFailed={failed}
                showDiagnostics={isSuperAdmin}
                testId="rpdm-hierarchy-flag-off"
            />
        );
    }

    if (loading) {
        return (
            <div
                role="status"
                aria-live="polite"
                data-testid="rpdm-hierarchy-loading"
                className="mx-auto flex w-full max-w-3xl flex-col gap-3 px-6 py-16 text-center text-sm text-slate-500"
            >
                <div className="mx-auto h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
                <div>Loading hierarchy…</div>
            </div>
        );
    }

    if (error) {
        const code = error.code || (error.response && error.response.data && error.response.data.detail && error.response.data.detail.code);
        if (error.code === "capability_missing") {
            return (
                <FailClosedPlaceholder
                    capabilityMissing="hierarchy"
                    showDiagnostics={isSuperAdmin}
                    testId="rpdm-hierarchy-capability-missing"
                />
            );
        }
        const refusal = translate(code);
        return (
            <FailClosedPlaceholder
                title={refusal.title || "Hierarchy unavailable"}
                message={refusal.body || "The hierarchy could not be loaded. Please try again shortly."}
                showDiagnostics={isSuperAdmin}
                testId="rpdm-hierarchy-error"
            />
        );
    }

    if (isEmpty(data)) {
        return (
            <div
                data-testid="hierarchy-empty"
                className="mx-auto flex w-full max-w-3xl flex-col items-center gap-2 px-6 py-16 text-center text-sm text-slate-500"
            >
                <div className="text-base font-medium text-slate-700">No hierarchy defined yet</div>
                <div>Workstreams, milestones, and work items appear here once they are created.</div>
            </div>
        );
    }

    return <HierarchyTree tree={data} />;
}
